import React from 'react';
import { Modal, PasswordInput, Select, Switch, TextInput } from '@mantine/core';
import { Button } from '../../../components/Button';
import type {
  PinpadFormState,
  TerminalCompanyChoice,
  TerminalFormState,
} from '../terminals-page.types';
import classes from '../terminals-page.module.scss';

type TerminalFormModalProps = {
  isOpen: boolean;
  isEditing: boolean;
  modalTitle: string;
  submitLabel: string;
  form: TerminalFormState;
  companies: TerminalCompanyChoice[];
  companyNameByTenantId: Map<string, string>;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onChangeForm: <K extends keyof TerminalFormState>(key: K, value: TerminalFormState[K]) => void;
  onChangePinpad: <K extends keyof PinpadFormState>(
    index: number,
    key: K,
    value: PinpadFormState[K],
  ) => void;
  onAddPinpad: () => void;
  onRemovePinpad: (index: number) => void;
};

export const TerminalFormModal: React.FC<TerminalFormModalProps> = ({
  isOpen,
  isEditing,
  modalTitle,
  submitLabel,
  form,
  companies,
  companyNameByTenantId,
  isSubmitting,
  onClose,
  onSubmit,
  onChangeForm,
  onChangePinpad,
  onAddPinpad,
  onRemovePinpad,
}) => {
  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={modalTitle}
      centered
      radius="md"
      size="lg"
      withinPortal
      closeOnClickOutside={false}
      trapFocus={false}
      closeButtonProps={{ 'data-testid': 'terminals-form-close' } as Record<string, string>}
    >
      <form onSubmit={onSubmit} className={classes.modalForm} noValidate>
        {isEditing ? (
          <TextInput
            label="חברה"
            value={companyNameByTenantId.get(form.tenantId) ?? form.tenantId}
            disabled
            size="md"
            radius="md"
          />
        ) : (
          <Select
            label="חברה"
            placeholder="בחר חברה"
            value={form.tenantId}
            onChange={(value) => onChangeForm('tenantId', value ?? '')}
            data={companies.map((company) => ({
              value: company.tenantId,
              label: company.companyName,
            }))}
            searchable
            required
            size="md"
            radius="md"
            data-testid="terminals-form-tenant"
          />
        )}

        <div className={classes.modalRow}>
          <TextInput
            label="שם המסוף"
            placeholder="שם המסוף"
            value={form.name}
            onChange={(event) => onChangeForm('name', event.currentTarget.value)}
            minLength={1}
            maxLength={120}
            required
            size="md"
            radius="md"
            data-testid="terminals-form-name"
          />
          <TextInput
            label="מספר מסוף"
            placeholder="מספר מסוף"
            value={form.terminalNumber}
            onChange={(event) => onChangeForm('terminalNumber', event.currentTarget.value)}
            minLength={1}
            maxLength={64}
            required
            size="md"
            radius="md"
            data-testid="terminals-form-terminal-number"
          />
        </div>

        <div className={classes.modalRow}>
          <PasswordInput
            label={isEditing ? 'סיסמת מסוף חדשה (אופציונלי)' : 'סיסמת מסוף'}
            placeholder="סיסמת מסוף"
            value={form.terminalPassword}
            onChange={(event) => onChangeForm('terminalPassword', event.currentTarget.value)}
            required={!isEditing}
            size="md"
            radius="md"
            data-testid="terminals-form-password"
          />
          <TextInput
            label="קוד מטבע"
            placeholder="1"
            value={form.currencyType}
            onChange={(event) => onChangeForm('currencyType', event.currentTarget.value)}
            maxLength={3}
            size="md"
            radius="md"
            data-testid="terminals-form-currency"
          />
        </div>

        {isEditing && (
          <Switch
            label="מסוף פעיל"
            checked={form.isActive}
            onChange={(event) => onChangeForm('isActive', event.currentTarget.checked)}
            data-testid="terminals-form-active"
          />
        )}

        <fieldset className={classes.pinpadsFieldset}>
          <legend className={classes.pinpadsLegend}>פינפדים</legend>
          <div className={classes.pinpadsList}>
            {form.pinpads.map((pinpad, index) => (
              <div key={index} className={classes.pinpadRow}>
                <TextInput
                  label="מזהה פינפד"
                  placeholder="pinpad-id"
                  value={pinpad.pinpadId}
                  onChange={(event) => onChangePinpad(index, 'pinpadId', event.currentTarget.value)}
                  size="sm"
                  radius="md"
                  data-testid={`terminals-form-pinpad-id-${index}`}
                />
                <TextInput
                  label="תווית"
                  placeholder="תווית"
                  value={pinpad.label}
                  onChange={(event) => onChangePinpad(index, 'label', event.currentTarget.value)}
                  size="sm"
                  radius="md"
                  data-testid={`terminals-form-pinpad-label-${index}`}
                />
                <Switch
                  className={classes.pinpadActiveToggle}
                  label="פעיל"
                  checked={pinpad.isActive}
                  onChange={(event) => onChangePinpad(index, 'isActive', event.currentTarget.checked)}
                  data-testid={`terminals-form-pinpad-active-${index}`}
                />
                <Switch
                  className={classes.pinpadActiveToggle}
                  label="הצעת טיפ"
                  checked={pinpad.tipEnabled}
                  onChange={(event) => onChangePinpad(index, 'tipEnabled', event.currentTarget.checked)}
                  data-testid={`terminals-form-pinpad-tip-${index}`}
                />
                <button
                  type="button"
                  className={`${classes.removePinpadButton} ${classes.pinpadRemoveButton}`}
                  onClick={() => onRemovePinpad(index)}
                  disabled={form.pinpads.length <= 1}
                  data-testid={`terminals-form-pinpad-remove-${index}`}
                >
                  הסר
                </button>
              </div>
            ))}
          </div>
          <button type="button" className={classes.addPinpadButton} onClick={onAddPinpad} data-testid="terminals-form-add-pinpad">
            הוסף פינפד
          </button>
        </fieldset>

        <div className={classes.modalActions}>
          <Button variant="cancel" onClick={onClose} disabled={isSubmitting} dataTestId="terminals-form-cancel">
            ביטול
          </Button>
          <Button type="submit" variant="save" loading={isSubmitting} loadingLabel="שומר..." dataTestId="terminals-form-submit">
            {submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
